import { Controller } from '@hotwired/stimulus'
import * as THREE from 'three'

/**
 * Interactive 3D Metal Print Controller
 *
 * Creates a realistic metal print with professional mounting hardware:
 * - Upload images to display on metal surface OR use provided src URL
 * - Realistic metal materials and lighting
 * - Detailed mounting system with foam backing and sawtooth hanger
 * - Interactive rotation with mouse/touch
 * - Zoom functionality with mouse wheel
 * - Subtle floating animation for dynamic presentation
 */
export default class extends Controller {
  static targets = ['container', 'imageUpload']

  static optionalTargets = ['imageUpload']

  static values = {
    src: String,
  }

  // ===== INITIALIZATION =====

  connect() {
    // remove element and clean up when the modal is closed
    window.addEventListener('ProductDetailsImageModalClosed', () => {
      this.element.remove()
    })

    // Auto-rotation settings
    this.autoRotate = true
    this.autoRotateSpeed = 0.002 // Slower for metal prints to feel more premium

    // Three.js core objects
    this.scene = null
    this.camera = null
    this.renderer = null
    this.metalPrint = null

    // Interaction state
    this.isDragging = false
    this.previousMousePosition = { x: 0, y: 0 }
    this.rotation = { x: 0, y: 0 }

    // Image properties
    this.currentImage = null
    this.currentAspectRatio = 4 / 3 // Default aspect ratio

    // Print dimensions for camera calculation
    this.printWidth = 3 // Default normalized width
    this.printHeight = 2.25 // Default normalized height (3 * 3/4)

    this.init()
    this.setupEventListeners()
  }

  disconnect() {
    this.element.removeEventListener('mousedown', this.onMouseDown)
    this.element.removeEventListener('mousemove', this.onMouseMove)
    this.element.removeEventListener('mouseup', this.onMouseUp)
    this.element.removeEventListener('wheel', this.onWheel)

    // Touch controls for mobile
    this.element.removeEventListener('touchstart', this.onTouchStart)
    this.element.removeEventListener('touchmove', this.onTouchMove)
    this.element.removeEventListener('touchend', this.onTouchEnd)

    // Clean up renderer and event listeners
    if (this.renderer) {
      this.containerTarget.removeChild(this.renderer.domElement)
    }
    window.removeEventListener('resize', this.onWindowResize)
  }

  /**
   * Initialize the complete Three.js scene
   */
  init() {
    this.setupScene()
    this.setupCamera()
    this.setupRenderer()
    this.setupLighting()
    this.createInitialMetalPrint()
    this.loadInitialImage()
    this.startAnimationLoop()
  }

  /**
   * Initialize the Three.js scene with white background
   */
  setupScene() {
    this.scene = new THREE.Scene()
    this.scene.background = new THREE.Color(0xffffff) // Clean white background
  }

  /**
   * Setup perspective camera for metal print viewing
   */
  setupCamera() {
    const container = this.containerTarget
    const rect = container.getBoundingClientRect()

    if (rect.width === 0 || rect.height === 0) {
      console.error('Container has no dimensions! Check your CSS.')
      this.camera = new THREE.PerspectiveCamera(75, 800 / 600, 0.1, 1000)
      this.camera.position.z = 5
      return
    }

    this.camera = new THREE.PerspectiveCamera(75, rect.width / rect.height, 0.1, 1000)
    this.setInitialCameraPosition()
  }

  /**
   * Calculate and set optimal camera position based on print dimensions
   */
  setInitialCameraPosition() {
    const printWidth = this.printWidth || 3
    const printHeight = this.printHeight || 2.25

    // Calculate distance needed to fit print in view
    const fov = this.camera.fov * (Math.PI / 180) // Convert to radians
    const maxDimension = Math.max(printWidth, printHeight)

    // Calculate distance with padding (multiply by 1.4 for better framing of metal prints)
    const distance = (maxDimension / 2) / Math.tan(fov / 2) * 1.4

    // Ensure reasonable distance range for metal prints
    const minDistance = Math.max(distance, 2.5)
    const maxDistance = Math.min(minDistance, 8)

    this.camera.position.z = maxDistance
  }

  /**
   * Initialize WebGL renderer with proper color management and shadows
   */
  setupRenderer() {
    this.renderer = new THREE.WebGLRenderer({ antialias: true })

    const container = this.containerTarget
    const rect = container.getBoundingClientRect()

    this.renderer.setSize(rect.width, rect.height)
    this.renderer.shadowMap.enabled = true
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap

    // Configure color management for proper image display
    this.renderer.outputColorSpace = THREE.SRGBColorSpace
    this.renderer.toneMapping = THREE.NoToneMapping
    this.renderer.toneMappingExposure = 1.0

    this.containerTarget.appendChild(this.renderer.domElement)
  }

  /**
   * Setup comprehensive lighting system for realistic metal appearance
   * Uses multiple light sources to properly illuminate metallic surfaces
   */
  setupLighting() {
    // High-intensity ambient light for overall scene brightness
    const ambientLight = new THREE.AmbientLight(0x606060, 12.2)
    this.scene.add(ambientLight)

    // Main directional light with high-resolution shadows
    const directionalLight = new THREE.DirectionalLight(0xffffff, 2.2)
    directionalLight.position.set(2, 2, 5)
    directionalLight.castShadow = true
    directionalLight.shadow.mapSize.width = 4096 // High-resolution shadows
    directionalLight.shadow.mapSize.height = 4096
    this.scene.add(directionalLight)

    // Point lights for even coverage and metallic reflections
    // Front-left point light
    const pointLight1 = new THREE.PointLight(0xffffff, 0.8, 10)
    pointLight1.position.set(-3, 2, 2)
    this.scene.add(pointLight1)

    // Front-right point light
    const pointLight2 = new THREE.PointLight(0xffffff, 0.8, 10)
    pointLight2.position.set(3, -2, 2)
    this.scene.add(pointLight2)

    // Back light for rim lighting effect
    const pointLight3 = new THREE.PointLight(0xffffff, 0.6, 10)
    pointLight3.position.set(0, 0, -4) // Behind the print
    this.scene.add(pointLight3)
  }

  /**
   * Create initial metal print with default aspect ratio
   */
  createInitialMetalPrint() {
    this.createMetalPrint(this.currentAspectRatio, 1)
  }

  /**
   * Load initial image - either from src or create default
   */
  loadInitialImage() {
    if (this.hasSrcValue && this.srcValue) {
      this.loadImageFromSrc(this.srcValue)
    }
    else {
      this.loadDefaultImage()
    }
  }

  /**
   * Load image from src URL
   * @param {string} srcUrl - URL of the image to load
   */
  loadImageFromSrc(srcUrl) {
    const img = new Image()
    img.crossOrigin = 'anonymous' // Handle CORS if needed
    img.onload = () => {
      const aspectRatio = img.width / img.height
      this.loadImage(srcUrl, aspectRatio)
    }
    img.onerror = (error) => {
      console.error(`Failed to load image from src: ${srcUrl}`, error)
      // Fall back to default image
      this.loadDefaultImage()
    }
    img.src = srcUrl
  }

  /**
   * Start the render loop
   */
  startAnimationLoop() {
    this.animate()
  }

  // ===== METAL PRINT CONSTRUCTION =====

  /**
   * Create the complete metal print assembly with realistic construction
   * @param {number} imageWidth - Image width (used for aspect ratio)
   * @param {number} imageHeight - Image height (used for aspect ratio)
   */
  createMetalPrint(imageWidth, imageHeight) {
  // Remove existing metal print
    if (this.metalPrint) {
      this.scene.remove(this.metalPrint)
    }

    this.metalPrint = new THREE.Group()

    // ===== CONFIGURATION & DIMENSIONS =====
    const sideDepth = 0.0002 // Very thin side edges
    const rearBoxDepthMultiplier = 0.09 // Mounting hardware depth

    // Normalize dimensions - make the larger dimension 3 units
    const maxDimension = Math.max(imageWidth, imageHeight)
    const scale = 3 / maxDimension
    const width = imageWidth * scale
    const height = imageHeight * scale
    const backDepth = sideDepth * 4 // Main print depth

    // Store dimensions for camera calculations
    this.printWidth = width
    this.printHeight = height

    // ===== MATERIAL DEFINITIONS =====
    const materials = this.createMaterials()

    // ===== CONSTRUCT METAL PRINT COMPONENTS =====
    this.createFrontFace(width, height, backDepth, materials.frontMaterial)
    this.createSideEdges(width, height, backDepth, sideDepth, materials.sideMaterial)
    this.createBackSurface(width, height, backDepth, materials.backMaterial)
    this.createMountingSystem(width, height, backDepth, rearBoxDepthMultiplier, materials)

    // ===== FINALIZATION =====
    this.finalizeMetalPrint()

    // Adjust camera for new print size
    this.adjustCameraForPrint()
  }

  /**
   * Adjust camera position based on current print dimensions
   */
  adjustCameraForPrint() {
    const fov = this.camera.fov * (Math.PI / 180) // Convert to radians
    const maxDimension = Math.max(this.printWidth, this.printHeight)

    // Calculate optimal distance with padding
    const distance = (maxDimension / 2) / Math.tan(fov / 2) * 1.4
    const targetZ = Math.max(distance, 2.5)
    const finalZ = Math.min(targetZ, 8)

    // Smoothly animate to new position
    const startZ = this.camera.position.z
    const duration = 1000 // 1 second for smooth transition
    const startTime = Date.now()

    const animateCamera = () => {
      const elapsed = Date.now() - startTime
      const progress = Math.min(elapsed / duration, 1)

      // Smooth easing function
      const easeProgress = progress * progress * (3 - 2 * progress)

      this.camera.position.z = startZ + (finalZ - startZ) * easeProgress

      if (progress < 1) {
        requestAnimationFrame(animateCamera)
      }
    }

    animateCamera()
  }

  /**
   * Create all material definitions for different parts of the metal print
   * @returns {Object} Object containing all material references
   */
  createMaterials() {
    return {
      // Front image surface - use MeshBasicMaterial for accurate color reproduction
      frontMaterial: new THREE.MeshBasicMaterial({
        color: 0xffffff,
        side: THREE.FrontSide,
      }),

      // Side edges - light gray with subtle metallic properties
      sideMaterial: new THREE.MeshStandardMaterial({
        color: 0xf0f0f0,
        metalness: 0.3,
        roughness: 0.7,
      }),

      // Main back surface - darker metal with higher reflectivity
      backMaterial: new THREE.MeshStandardMaterial({
        color: 0xa8a8a8,
        metalness: 0.7,
        roughness: 0.3,
      }),

      // Foam backing - dark matte material
      foamMaterial: new THREE.MeshStandardMaterial({
        color: 0x1a1a1a, // Dark grey/black foam
        metalness: 0.0,
        roughness: 0.9,
      }),

      // Hanging hardware - shiny metal
      hangerMaterial: new THREE.MeshStandardMaterial({
        color: 0x444444,
        metalness: 0.9,
        roughness: 0.1,
      }),
    }
  }

  /**
   * Create the front face where the image is displayed
   * @param {number} width - Print width
   * @param {number} height - Print height
   * @param {number} backDepth - Overall print depth
   * @param {THREE.Material} frontMaterial - Material for front face
   */
  createFrontFace(width, height, backDepth, frontMaterial) {
    const frontGeometry = new THREE.PlaneGeometry(width, height)
    const front = new THREE.Mesh(frontGeometry, frontMaterial)
    front.position.z = backDepth / 2 // Position at front of print
    this.metalPrint.add(front)

    // Store reference for texture updates
    this.metalPrint.frontFace = front
  }

  /**
   * Create the thin metallic side edges of the print
   * @param {number} width - Print width
   * @param {number} height - Print height
   * @param {number} backDepth - Overall print depth
   * @param {number} sideDepth - Thickness of side edges
   * @param {THREE.Material} sideMaterial - Material for side edges
   */
  createSideEdges(width, height, backDepth, sideDepth, sideMaterial) {
    // Top edge
    const topGeometry = new THREE.PlaneGeometry(width, backDepth)
    const top = new THREE.Mesh(topGeometry, sideMaterial)
    top.position.y = height / 2
    top.position.z = 0 // Center in the depth
    top.rotation.x = -Math.PI / 2 // Rotate to face upward
    this.metalPrint.add(top)

    // Bottom edge
    const bottom = new THREE.Mesh(topGeometry.clone(), sideMaterial)
    bottom.position.y = -height / 2
    bottom.position.z = 0
    bottom.rotation.x = Math.PI / 2 // Rotate to face downward
    this.metalPrint.add(bottom)

    // Left edge - using BoxGeometry for better visibility
    const leftGeometry = new THREE.BoxGeometry(sideDepth, height, backDepth)
    const left = new THREE.Mesh(leftGeometry, sideMaterial)
    left.position.x = -width / 2
    left.position.z = 0
    this.metalPrint.add(left)

    // Right edge - using BoxGeometry for better visibility
    const rightGeometry = new THREE.BoxGeometry(sideDepth, height, backDepth)
    const right = new THREE.Mesh(rightGeometry, sideMaterial)
    right.position.x = width / 2
    right.position.z = 0
    this.metalPrint.add(right)
  }

  /**
   * Create the main metal back surface
   * @param {number} width - Print width
   * @param {number} height - Print height
   * @param {number} backDepth - Overall print depth
   * @param {THREE.Material} backMaterial - Material for back surface
   */
  createBackSurface(width, height, backDepth, backMaterial) {
    const backGeometry = new THREE.BoxGeometry(width, height, 0.01) // Thin metal back
    const back = new THREE.Mesh(backGeometry, backMaterial)
    back.position.z = -backDepth / 2 - 0.006 // Offset to prevent z-fighting
    this.metalPrint.add(back)
  }

  /**
   * Create the complete mounting system with foam backing and metal veneer
   * @param {number} width - Print width
   * @param {number} height - Print height
   * @param {number} backDepth - Overall print depth
   * @param {number} rearBoxDepthMultiplier - Depth multiplier for mounting hardware
   * @param {Object} materials - All material references
   */
  createMountingSystem(width, height, backDepth, rearBoxDepthMultiplier, materials) {
    const mountBoxDepth = rearBoxDepthMultiplier
    const mountBoxWidth = width * 0.4 // 40% of print width
    const mountBoxHeight = height * 0.3 // 30% of print height

    // Create foam backing base
    this.createFoamBacking(width, height, backDepth, mountBoxWidth, mountBoxHeight, mountBoxDepth, materials.foamMaterial)

    // Create metal veneer with sawtooth hanger hole
    this.createMetalVeneerWithHangerHole(width, height, backDepth, mountBoxWidth, mountBoxHeight, mountBoxDepth, materials.backMaterial)
  }

  /**
   * Create the black foam backing that provides mounting depth
   * @param {number} width - Print width
   * @param {number} height - Print height
   * @param {number} backDepth - Overall print depth
   * @param {number} mountBoxWidth - Width of mounting hardware
   * @param {number} mountBoxHeight - Height of mounting hardware
   * @param {number} mountBoxDepth - Depth of mounting hardware
   * @param {THREE.Material} foamMaterial - Foam material
   */
  createFoamBacking(width, height, backDepth, mountBoxWidth, mountBoxHeight, mountBoxDepth, foamMaterial) {
    const foamGeometry = new THREE.BoxGeometry(mountBoxWidth, mountBoxHeight, mountBoxDepth)
    const foamBase = new THREE.Mesh(foamGeometry, foamMaterial)
    // Position in upper portion of print for realistic mounting
    foamBase.position.set(0, height * 0.3, (-backDepth / 2) - (mountBoxDepth / 2) - 0.002)
    this.metalPrint.add(foamBase)
  }

  /**
   * Create metal veneer on foam backing with sawtooth hanger cutout
   * @param {number} width - Print width
   * @param {number} height - Print height
   * @param {number} backDepth - Overall print depth
   * @param {number} mountBoxWidth - Width of mounting hardware
   * @param {number} mountBoxHeight - Height of mounting hardware
   * @param {number} mountBoxDepth - Depth of mounting hardware
   * @param {THREE.Material} backMaterial - Metal veneer material
   */
  createMetalVeneerWithHangerHole(width, height, backDepth, mountBoxWidth, mountBoxHeight, mountBoxDepth, backMaterial) {
    const veneerThickness = 0.01
    const hangerHoleWidth = mountBoxWidth * 0.4 // Sawtooth hanger width
    const hangerHoleHeight = height * 0.06 // Sawtooth hanger height
    const gapSize = 0.0 // No gap around hanger hole

    // Calculate Z position for veneer (back face of foam)
    const veneerZ = (-backDepth / 2) - mountBoxDepth + (veneerThickness / 2) - 0.012

    // Create veneer in 4 pieces around the sawtooth hanger hole

    // Top piece (above hanger hole)
    const topPieceHeight = (mountBoxHeight - hangerHoleHeight) / 2 - gapSize
    const topVeneer = new THREE.BoxGeometry(mountBoxWidth, topPieceHeight, veneerThickness)
    const topVeneerMesh = new THREE.Mesh(topVeneer, backMaterial)
    topVeneerMesh.position.set(
      0,
      height * 0.3 + (hangerHoleHeight / 2) + gapSize + (topPieceHeight / 2),
      veneerZ,
    )
    this.metalPrint.add(topVeneerMesh)

    // Bottom piece (below hanger hole)
    const bottomVeneer = new THREE.BoxGeometry(mountBoxWidth, topPieceHeight, veneerThickness)
    const bottomVeneerMesh = new THREE.Mesh(bottomVeneer, backMaterial)
    bottomVeneerMesh.position.set(
      0,
      height * 0.3 - (hangerHoleHeight / 2) - gapSize - (topPieceHeight / 2),
      veneerZ,
    )
    this.metalPrint.add(bottomVeneerMesh)

    // Left piece (left of hanger hole)
    const sidePieceWidth = (mountBoxWidth - hangerHoleWidth) / 2 - gapSize
    const leftVeneer = new THREE.BoxGeometry(sidePieceWidth, hangerHoleHeight, veneerThickness)
    const leftVeneerMesh = new THREE.Mesh(leftVeneer, backMaterial)
    leftVeneerMesh.position.set(
      -(hangerHoleWidth / 2) - gapSize - (sidePieceWidth / 2),
      height * 0.3,
      veneerZ,
    )
    this.metalPrint.add(leftVeneerMesh)

    // Right piece (right of hanger hole)
    const rightVeneer = new THREE.BoxGeometry(sidePieceWidth, hangerHoleHeight, veneerThickness)
    const rightVeneerMesh = new THREE.Mesh(rightVeneer, backMaterial)
    rightVeneerMesh.position.set(
      (hangerHoleWidth / 2) + gapSize + (sidePieceWidth / 2),
      height * 0.3,
      veneerZ,
    )
    this.metalPrint.add(rightVeneerMesh)
  }

  /**
   * Finalize metal print setup - add to scene, enable shadows, apply current image
   */
  finalizeMetalPrint() {
    this.scene.add(this.metalPrint)

    // Enable shadows for all mesh components
    this.metalPrint.traverse((child) => {
      if (child.isMesh) {
        child.castShadow = true
        child.receiveShadow = true
      }
    })

    // Apply current image texture if one exists
    if (this.currentImage) {
      this.metalPrint.frontFace.material.map = this.currentImage
      this.metalPrint.frontFace.material.needsUpdate = true
    }
  }

  // ===== IMAGE HANDLING =====

  /**
   * Load an image texture onto the metal print with proper color management
   * @param {string} src - Image source (URL or data URL)
   * @param {number} aspectRatio - Optional aspect ratio override
   */
  loadImage(src, aspectRatio = null) {
    const img = new Image()

    img.crossOrigin = 'anonymous'

    img.onload = () => {
    // Create texture with proper color space settings
      const texture = new THREE.Texture(img)
      texture.needsUpdate = true
      texture.colorSpace = THREE.SRGBColorSpace
      texture.flipY = true // Proper orientation
      texture.premultiplyAlpha = false
      texture.format = THREE.RGBAFormat // Required for sRGB color space
      texture.type = THREE.UnsignedByteType // Required for sRGB

      this.currentImage = texture

      // Calculate aspect ratio from image or use provided ratio
      let imageAspectRatio = aspectRatio
      if (!imageAspectRatio) {
        imageAspectRatio = img.width / img.height
      }

      this.currentAspectRatio = imageAspectRatio

      // Recreate metal print with correct aspect ratio and smart zoom
      this.createMetalPrint(imageAspectRatio, 1)
    }

    img.onerror = (error) => {
      console.error('Failed to load image:', error)
    }

    img.src = src
  }

  /**
   * Create and load a default placeholder image
   * Generates a gradient background with sample text
   */
  loadDefaultImage() {
    const canvas = document.createElement('canvas')
    const width = 400
    const height = 300
    const aspectRatio = 4 / 3

    canvas.width = width
    canvas.height = height

    const dataURL = canvas.toDataURL()
    this.loadImage(dataURL, aspectRatio)
  }

  // ===== EVENT HANDLING =====

  /**
   * Setup all event listeners for interaction
   */
  setupEventListeners() {
    // File upload handling - only if target exists
    if (this.hasImageUploadTarget) {
      this.imageUploadTarget.addEventListener('change', this.handleImageUpload.bind(this))
    }

    // Mouse controls for desktop
    this.element.addEventListener('mousedown', this.onMouseDown)
    this.element.addEventListener('mousemove', this.onMouseMove)
    this.element.addEventListener('mouseup', this.onMouseUp)
    this.element.addEventListener('wheel', this.onWheel)

    // Touch controls for mobile
    this.element.addEventListener('touchstart', this.onTouchStart)
    this.element.addEventListener('touchmove', this.onTouchMove)
    this.element.addEventListener('touchend', this.onTouchEnd)

    // Window resize handling
    this.onWindowResize = this.onWindowResize.bind(this)
    window.addEventListener('resize', this.onWindowResize)
  }

  /**
   * Handle image file upload
   * @param {Event} event - File input change event
   */
  handleImageUpload(event) {
    // Don't allow file upload if src is provided
    if (this.hasSrcValue && this.srcValue) {
      return
    }

    const file = event.target.files[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (e) => {
        const img = new Image()
        img.onload = () => {
          const aspectRatio = img.width / img.height
          this.loadImage(e.target.result, aspectRatio)
        }
        img.src = e.target.result
      }
      reader.readAsDataURL(file)
    }
  }

  // ===== MOUSE INTERACTION =====

  /**
   * Handle mouse press - start rotation interaction
   * @param {MouseEvent} event - Mouse down event
   */
  onMouseDown = (event) => {
    this.isDragging = true
    this.previousMousePosition = {
      x: event.clientX,
      y: event.clientY,
    }
    // Pause auto-rotation during manual interaction
    this.autoRotate = false
  }

  /**
   * Handle mouse movement during drag - rotate metal print
   * @param {MouseEvent} event - Mouse move event
   */
  onMouseMove = (event) => {
    if (this.isDragging && this.metalPrint) {
      const deltaMove = {
        x: event.clientX - this.previousMousePosition.x,
        y: event.clientY - this.previousMousePosition.y,
      }

      // Update rotation values
      this.rotation.y += deltaMove.x * 0.01 // Horizontal rotation
      this.rotation.x += deltaMove.y * 0.01 // Vertical rotation

      // Limit vertical rotation to prevent flipping upside down
      this.rotation.x = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, this.rotation.x))

      // Apply rotation to metal print
      this.metalPrint.rotation.x = this.rotation.x
      this.metalPrint.rotation.y = this.rotation.y

      this.previousMousePosition = {
        x: event.clientX,
        y: event.clientY,
      }
    }
  }

  /**
   * Handle mouse release - end rotation interaction
   */
  onMouseUp = () => {
    this.isDragging = false

    // Resume auto-rotation after a short delay
    setTimeout(() => {
      if (!this.isDragging) {
        this.autoRotate = true
      }
    }, 3000)
  }

  /**
   * Handle mouse wheel for zoom control
   * @param {WheelEvent} event - Wheel event
   */
  onWheel = (event) => {
    event.preventDefault()
    const zoomSpeed = 0.1
    this.camera.position.z += event.deltaY * 0.01 * zoomSpeed
    // Constrain zoom between 2 and 10 units
    this.camera.position.z = Math.max(2, Math.min(10, this.camera.position.z))
  }

  // ===== TOUCH INTERACTION =====

  /**
   * Handle touch start for mobile rotation
   * @param {TouchEvent} event - Touch start event
   */
  onTouchStart = (event) => {
    if (event.touches.length === 1) {
      this.isDragging = true
      this.previousMousePosition = {
        x: event.touches[0].clientX,
        y: event.touches[0].clientY,
      }
      // Pause auto-rotation during touch interaction
      this.autoRotate = false
    }
  }

  /**
   * Handle touch movement for mobile rotation
   * @param {TouchEvent} event - Touch move event
   */
  onTouchMove = (event) => {
    event.preventDefault()
    if (this.isDragging && event.touches.length === 1 && this.metalPrint) {
      const deltaMove = {
        x: event.touches[0].clientX - this.previousMousePosition.x,
        y: event.touches[0].clientY - this.previousMousePosition.y,
      }

      // Update rotation (same logic as mouse)
      this.rotation.y += deltaMove.x * 0.01
      this.rotation.x += deltaMove.y * 0.01
      this.rotation.x = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, this.rotation.x))

      this.metalPrint.rotation.x = this.rotation.x
      this.metalPrint.rotation.y = this.rotation.y

      this.previousMousePosition = {
        x: event.touches[0].clientX,
        y: event.touches[0].clientY,
      }
    }
  }

  /**
   * Handle touch end - stop mobile rotation
   */
  onTouchEnd = () => {
    this.isDragging = false

    // Resume auto-rotation after delay
    setTimeout(() => {
      if (!this.isDragging) {
        this.autoRotate = true
      }
    }, 3000)
  }

  // ===== WINDOW EVENTS =====

  /**
   * Handle window resize - update camera and renderer
   */
  onWindowResize() {
    const container = this.containerTarget
    const rect = container.getBoundingClientRect()

    this.camera.aspect = rect.width / rect.height
    this.camera.updateProjectionMatrix()
    this.renderer.setSize(rect.width, rect.height)

    // Recalculate optimal camera position for new container size
    if (this.metalPrint && this.printWidth && this.printHeight) {
      this.adjustCameraForPrint()
    }
    else {
    // If no print created yet, use default dimensions
      this.setInitialCameraPosition()
    }
  }

  // ===== ANIMATION =====

  /**
   * Main animation loop with subtle floating effect
   */
  animate() {
    requestAnimationFrame(this.animate.bind(this))

    if (this.metalPrint) {
    // Add subtle floating animation for dynamic presentation
      this.metalPrint.position.y = Math.sin(Date.now() * 0.001) * 0.02

      // Auto-rotate the metal print if enabled and not being manually dragged
      if (this.autoRotate && !this.isDragging) {
        this.rotation.y += this.autoRotateSpeed
        this.metalPrint.rotation.y = this.rotation.y
      }
    }

    this.renderer.render(this.scene, this.camera)
  }
}
